/** The native Android shell (waki-mail-android), app-wide.
 *
 *  From 0.7.0 (protocol 3) the shell is a plain host and the phone app
 *  (src/phone) draws everything. Shells 0.5 and 0.6 (protocol 1 and 2) draw
 *  their own top bar, bottom bar and action button; there the phone app
 *  hides its own bar and answers the shell's taps through
 *  `window.__wovShell` (PhoneApp.tsx). The page talks back through
 *  `window.AndroidShell`. A screen only removes handlers still its own. */

type AndroidShell = {
  setDrawer: (json: string) => void;
  setTitle: (title: string) => void;
  setSelected: (id: string) => void;
  shellVersion: () => number;
  /** Protocol 4 (Android 0.8.5). */
  openUrl?: (url: string) => boolean;
  openFile?: (name: string, mime: string, base64: string, share: boolean) => boolean;
  /** Protocol 5 (Android 0.9.0): the home-screen calendar widgets. */
  refreshWidgets?: () => void;
  pinWidget?: (kind: string) => boolean;
};

export type ShellHandlers = {
  /** A drawer entry was tapped (mail folders and views). */
  select: (id: string) => void;
  /** The action button: compose on Mail, a new task on Tasks. */
  compose: () => void;
  /** A bottom-bar tab: "mail", "tasks", "search", "more" (and "calendar" from old shells). */
  openTab: (tab: string) => void;
};

declare global {
  interface Window {
    AndroidShell?: AndroidShell;
    __wovShell?: ShellHandlers;
  }
}

/** True when running inside the native shell. The bridge's existence is the
 *  signal: same origin and user agent as a browser otherwise. */
export function inNativeShell(): boolean {
  return typeof window !== "undefined" && typeof window.AndroidShell?.setDrawer === "function";
}

/** The shell's protocol version (0 outside the shell; 2 has the Tasks tab). */
export function shellVersion(): number {
  try {
    return window.AndroidShell?.shellVersion?.() ?? 0;
  } catch {
    return 0;
  }
}

/** Install the handlers the shell calls into. Returns a cleanup function. */
export function installShellHandlers(handlers: ShellHandlers): () => void {
  window.__wovShell = handlers;
  return () => {
    if (window.__wovShell === handlers) delete window.__wovShell;
  };
}

/** The native top bar's title, and which drawer entry is marked. */
export function setShellTitle(title: string, selectedId = ""): void {
  const shell = window.AndroidShell;
  if (!shell) return;
  try {
    shell.setTitle(title);
    shell.setSelected(selectedId);
  } catch {
    // A bridge that throws is a broken shell; the page still works.
  }
}

/** A link from a message: the system's app for it (Android 0.8.5), else a
 *  new browser tab. */
export function openExternal(url: string): void {
  const shell = window.AndroidShell;
  if (shell?.openUrl && shellVersion() >= 4) {
    try {
      if (shell.openUrl(url)) return;
    } catch {
      /* fall through to the browser */
    }
  }
  window.open(url, "_blank", "noopener");
}

/** Open (or share) a file the page has in hand: in the Android app, the app
 *  that reads that kind of file; in a browser, a download (or the share
 *  sheet). Returns false when it could not be handed over. */
export async function openFileOnDevice(blob: Blob, name: string, share = false): Promise<boolean> {
  const shell = window.AndroidShell;
  if (shell?.openFile && shellVersion() >= 4) {
    const base64 = await blobToBase64(blob);
    try {
      return shell.openFile(name, blob.type || "application/octet-stream", base64, share);
    } catch {
      return false;
    }
  }
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean; share?: (d: { files: File[]; title?: string }) => Promise<void> };
  if (share && nav.share && nav.canShare) {
    const file = new File([blob], name, { type: blob.type });
    if (nav.canShare({ files: [file] })) {
      try {
        await nav.share({ files: [file], title: name });
        return true;
      } catch {
        return false;
      }
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ""));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

let widgetsAskedAt = 0;
/** The calendar changed here: the Android home-screen widgets fetch it
 *  again (at most every 30 seconds from the web side). */
export function refreshShellWidgets(): void {
  const shell = window.AndroidShell;
  if (!shell?.refreshWidgets || shellVersion() < 5) return;
  const now = Date.now();
  if (now - widgetsAskedAt < 30_000) return;
  widgetsAskedAt = now;
  try {
    shell.refreshWidgets();
  } catch {
    /* an older shell */
  }
}

/** Android 0.9.0 and later can put a calendar widget on the home screen. */
export function canPinWidgets(): boolean {
  return !!window.AndroidShell?.pinWidget && shellVersion() >= 5;
}

/** Ask Android to add a widget ("next" or "agenda") to the home screen. */
export function pinShellWidget(kind: "next" | "agenda"): boolean {
  try {
    return !!window.AndroidShell?.pinWidget?.(kind);
  } catch {
    return false;
  }
}
